import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bot, ExternalLink, Loader2, Send, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const hiddenPath = (pathname) => (
  pathname === '/'
  || pathname.startsWith('/login')
  || pathname.startsWith('/register')
  || pathname.startsWith('/auth')
  || pathname.startsWith('/onboarding')
  || pathname.startsWith('/forgot-password')
  || pathname.startsWith('/reset-password')
);

const activityText = (item) => (
  item?.summary
  || item?.message
  || item?.action
  || item?.title
  || 'Atlas completed an activity.'
);

const AtlasFloatingButton = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [activity, setActivity] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const shouldHide = loading || !user || hiddenPath(location.pathname);

  useEffect(() => {
    if (!open || shouldHide) return;
    let cancelled = false;
    const loadActivity = async () => {
      setError('');
      try {
        const res = await fetch(`${API_URL}/api/atlas/activity?limit=3`, { headers: authHeaders() });
        if (!res.ok) throw new Error(`Activity failed: ${res.status}`);
        const data = await res.json();
        const rows = Array.isArray(data)
          ? data
          : Array.isArray(data?.activity)
            ? data.activity
            : Array.isArray(data?.items)
              ? data.items
              : data?.latest
                ? [data.latest]
                : [];
        if (!cancelled) setActivity(rows.slice(0, 3));
      } catch (err) {
        console.error('Atlas floating activity failed', err);
        if (!cancelled) setError('Could not load recent activity.');
      }
    };
    loadActivity();
    return () => { cancelled = true; };
  }, [open, shouldHide]);

  const sendInstruction = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/atlas/instruct`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ instruction: text }),
      });
      if (!res.ok) throw new Error(`Instruction failed: ${res.status}`);
      setInput('');
      setActivity((prev) => [{ summary: `Instruction sent: ${text}` }, ...prev].slice(0, 3));
    } catch (err) {
      console.error('Atlas floating instruction failed', err);
      setError('Atlas could not send that instruction.');
    } finally {
      setBusy(false);
    }
  };

  if (shouldHide) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[120]" data-testid="atlas-floating-button">
      {open && (
        <div
          className="mb-3 w-[340px] max-w-[calc(100vw-48px)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
          data-testid="atlas-floating-drawer"
        >
          <div className="flex items-center justify-between border-b border-slate-200 bg-[#F8F7F4] px-4 py-3">
            <div>
              <div className="flex items-center gap-2 text-sm font-black text-[#0D1F3C]">
                <span className="h-2.5 w-2.5 rounded-full bg-[#005249]" />
                Atlas is running
              </div>
              <p className="mt-0.5 text-xs text-slate-500">AI BD Director</p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-md p-1 text-slate-500 hover:bg-slate-200 hover:text-[#0D1F3C]"
              aria-label="Close Atlas"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-3 p-4">
            <div>
              <div className="mb-2 text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Recent Activity</div>
              <div className="space-y-2">
                {activity.length > 0 ? activity.map((item, index) => (
                  <div key={item.id || item._id || index} className="rounded-lg border border-slate-200 bg-[#F8F7F4] px-3 py-2 text-xs font-semibold text-slate-700">
                    {activityText(item)}
                  </div>
                )) : (
                  <div className="rounded-lg border border-slate-200 bg-[#F8F7F4] px-3 py-2 text-xs text-slate-500">
                    No recent Atlas activity.
                  </div>
                )}
              </div>
              {error && <p className="mt-2 text-xs font-semibold text-red-600">{error}</p>}
            </div>

            <div className="flex items-center gap-2">
              <input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={(event) => { if (event.key === 'Enter') sendInstruction(); }}
                placeholder="Ask Atlas..."
                className="min-h-10 flex-1 rounded-lg border border-slate-200 bg-white px-3 text-sm text-[#0D1F3C] outline-none focus:border-[#005249]"
              />
              <button
                type="button"
                onClick={sendInstruction}
                disabled={busy || !input.trim()}
                className="flex h-10 w-10 items-center justify-center rounded-lg text-white disabled:opacity-50"
                style={{ background: '#005249' }}
                aria-label="Send to Atlas"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>

            <Link to="/atlas" onClick={() => setOpen(false)} className="inline-flex items-center gap-1 text-xs font-black text-[#0D1F3C] hover:underline">
              Open Atlas <ExternalLink className="h-3 w-3" />
            </Link>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex h-14 w-14 items-center justify-center rounded-full text-white shadow-xl transition-transform hover:scale-105"
        style={{ background: '#005249' }}
        aria-label="Open Atlas"
      >
        <Bot className="h-6 w-6" />
      </button>
    </div>
  );
};

export default AtlasFloatingButton;
